import Phaser from 'phaser';

import Button from '../button/Button';
import RankButton from './RankButton';
import configs from '../../configs/configs';
import { fontFamilies } from '../../../../utils/styles';

const { width, height } = configs;

const px = 40;
const buttonSize = 186;
const verticalGap = buttonSize + 50;

class InfoButtons extends Phaser.GameObjects.Container {
  numberOfSpins = 0;
  starterPackAvailable = false;

  constructor(
    scene,
    y,
    {
      noBackground = false,
      hideSettings = false,
      hideAuction = false,
      hideLeaderboard = false,
      hideAugments = false,
      hideReward = false,
      referralDisabled = false,
      airdropDisabled = false,
      starterPackDisabled = false,
      onClickAuction,
      onClickReward,
      onClickSpin,
    } = {}
  ) {
    super(scene, 0, 0);

    this.scene = scene;

    if (!hideSettings) {
      this.settingButton = new Button(
        scene,
        buttonSize / 2 + px,
        y,
        'button-setting',
        'button-setting-pressed',
        () => {
          scene.popupSettings?.open();
        },
        { sound: 'button-1' }
      );
      this.add(this.settingButton);
    }

    if (!hideAuction) {
      this.auctionHouseButton = new Button(
        scene,
        buttonSize / 2 + px,
        y + verticalGap,
        'button-auction-house',
        'button-auction-house-pressed',
        () => {
          if (onClickAuction) {
            onClickAuction();
            return;
          }
          this.scene.scene.pause('MainScene');
          const isPaused = this.scene.scene.isPaused('AuctionScene');
          if (isPaused) {
            this.scene.scene.resume('AuctionScene');
            const auctionScene = this.scene.scene.get('AuctionScene');
            auctionScene?.refreshItems?.(true);
          } else {
            this.scene.scene.launch('AuctionScene');
          }
          this.scene.scene.bringToTop('AuctionScene');
        },
        { sound: 'button-1' }
      );
      this.add(this.auctionHouseButton);
    }

    this.rewardButton = new Button(
      scene,
      buttonSize / 2 + px,
      y + 2 * verticalGap,
      'button-reward',
      'button-reward-pressed',
      () => {
        if (onClickReward) {
          onClickReward();
          this.showPopupReward();
          return;
        }
        this.scene.events.emit('s-get-starter-pack-available', { packId: 'default' });
        this.togglePopupReward();
      },
      { sound: 'button-1' }
    );
    this.rewardButton.setVisible(!hideReward);

    if (!hideLeaderboard) {
      this.rankButton = new RankButton(
        scene,
        width - px - buttonSize / 2,
        y,
        'button-rank',
        'button-rank-pressed',
        () => {
          scene.popupLeaderboard?.open();
        },
        { sound: 'button-1' }
      );
      this.add(this.rankButton);
    }

    if (!hideAugments) {
      this.augmentsButton = new Button(
        scene,
        width - px - buttonSize / 2,
        y + verticalGap,
        'button-augments',
        'button-augments-pressed',
        () => {
          this.openAugmentsPopup();
        },
        { sound: 'button-1', disabledImage: 'button-augments-disabled' }
      );
      this.add(this.augmentsButton);
    }

    this.badge = scene.add
      .image(
        this.rewardButton.x + this.rewardButton.width / 2 - 10,
        this.rewardButton.y - this.rewardButton.height / 2 + 10,
        'badge'
      )
      .setOrigin(0.5, 0.5);

    this.badgeText = scene.add
      .text(this.badge.x, this.badge.y, '0', { fontFamily: fontFamilies.extraBold, fontSize: '46px', color: '#fff' })
      .setOrigin(0.5, 0.5);

    this.arrow = scene.add
      .sprite(this.rewardButton.x, this.rewardButton.y + this.rewardButton.height / 2, 'arrow-down-gold')
      .setOrigin(0.5, 0.5)
      .setVisible(!hideReward);

    this.referralButton = new Button(
      scene,
      buttonSize / 2 + px,
      y + 3 * verticalGap,
      'button-referral',
      'button-referral-pressed',
      () => {
        this.openReferralPopup();
      },
      { sound: 'button-1', disabledImage: 'button-referral-disabled' }
    );
    this.referralButton.setDisabledState(referralDisabled);

    this.airdropButton = new Button(
      scene,
      buttonSize / 2 + px,
      y + 4 * verticalGap,
      'button-airdrop',
      'button-airdrop-pressed',
      () => {
        this.openAirdropPopup();
      },
      { sound: 'button-1', disabledImage: 'button-airdrop-disabled' }
    );
    this.airdropButton.setDisabledState(airdropDisabled);

    this.dailySpinButton = new Button(
      scene,
      buttonSize / 2 + px,
      y + 5 * verticalGap,
      'button-daily-spin',
      'button-daily-spin-pressed',
      () => {
        // within simulator
        if (onClickSpin) {
          onClickSpin();
          return;
        }
        this.openDailySpinPopup();
      },
      { sound: 'button-1', disabledImage: 'button-daily-spin-disabled' }
    );

    this.starterPackButton = new Button(
      scene,
      buttonSize / 2 + px,
      y + 6 * verticalGap,
      'button-starter-pack',
      'button-starter-pack-pressed',
      () => {
        this.openStarterPackPopup();
      },
      { sound: 'button-1', disabledImage: 'button-starter-pack-disabled' }
    );
    this.starterPackButton.setDisabledState(starterPackDisabled);

    this.dailySpinBadge = scene.add
      .image(
        this.dailySpinButton.x + this.dailySpinButton.width / 2 - 10,
        this.dailySpinButton.y - this.dailySpinButton.height / 2 + 10,
        'badge'
      )
      .setOrigin(0.5, 0.5);
    this.dailySpinBadgeText = scene.add
      .text(this.dailySpinBadge.x, this.dailySpinBadge.y, '0', {
        fontFamily: fontFamilies.extraBold,
        fontSize: '46px',
        color: '#fff',
      })
      .setOrigin(0.5, 0.5);

    this.referralText = scene.add
      .text(this.referralButton.x + this.referralButton.width / 2 + 50, this.referralButton.y, 'Referrals', {
        fontSize: '46px',
        fontFamily: fontFamilies.extraBold,
        color: referralDisabled ? '#aaa' : '#fff',
      })
      .setOrigin(0, 0.5);

    this.airdropText = scene.add
      .text(this.airdropButton.x + this.airdropButton.width / 2 + 50, this.airdropButton.y, 'Airdrop', {
        fontSize: '46px',
        fontFamily: fontFamilies.extraBold,
        color: airdropDisabled ? '#aaa' : '#fff',
      })
      .setOrigin(0, 0.5);

    this.dailySpinText = scene.add
      .text(this.dailySpinButton.x + this.dailySpinButton.width / 2 + 50, this.dailySpinButton.y, 'Spin to Win', {
        fontSize: '46px',
        fontFamily: fontFamilies.extraBold,
        color: '#fff',
      })
      .setOrigin(0, 0.5);

    this.starterPackText = scene.add
      .text(
        this.starterPackButton.x + this.starterPackButton.width / 2 + 50,
        this.starterPackButton.y,
        'Get Beginners Pack',
        {
          fontSize: '46px',
          fontFamily: fontFamilies.extraBold,
          color: starterPackDisabled ? '#aaa' : '#fff',
        }
      )
      .setOrigin(0, 0.5);

    if (!noBackground) {
      this.background = scene.add.rectangle(0, 0, width, height, 0x260343, 0.8).setOrigin(0, 0).setVisible(false);
      this.background.setInteractive().on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => {
        this.hidePopupReward();
      });
      this.add(this.background);
    }

    this.hideBadge();
    this.hidePopupReward();

    this.add(this.rewardButton);
    this.add(this.badge);
    this.add(this.badgeText);
    this.add(this.arrow);
    this.add(this.referralButton);
    this.add(this.airdropButton);
    this.add(this.dailySpinButton);
    this.add(this.dailySpinBadge);
    this.add(this.dailySpinBadgeText);
    this.add(this.starterPackButton);
    this.add(this.referralText);
    this.add(this.airdropText);
    this.add(this.dailySpinText);
    this.add(this.starterPackText);

    scene.events.on('s-set-starter-pack-available', ({ starterPackAvailable }) => {
      this.starterPackAvailable = starterPackAvailable;
    });

    scene.events.emit('s-get-starter-pack-available', { packId: 'default' });
    scene.events.on(
      's-set-season-status',
      ({ status }) => {
        this.status = status;
        console.log('s-set-season-status', { status });

        if (status !== 'open') {
          this.augmentsButton?.setDisabledState(true);
        } else {
          this.augmentsButton?.setDisabledState(false);
        }
      },
      this
    );
  }

  hideBadge() {
    this.badge?.setVisible(false);
    this.badgeText?.setVisible(false);
    this.dailySpinBadge?.setVisible(false);
    this.dailySpinBadgeText?.setVisible(false);
  }

  showBadge() {
    this.badge?.setVisible(true);
    this.badgeText?.setVisible(true);

    if (this.dailySpinButton?.visible) {
      this.dailySpinBadge?.setVisible(true);
      this.dailySpinBadgeText?.setVisible(true);
    }
  }

  hidePopupReward() {
    this.referralButton?.setVisible(false);
    this.airdropButton?.setVisible(false);
    this.dailySpinButton?.setVisible(false);
    this.starterPackButton?.setVisible(false);
    this.referralText?.setVisible(false);
    this.dailySpinText?.setVisible(false);
    this.airdropText?.setVisible(false);
    this.dailySpinBadge?.setVisible(false);
    this.dailySpinBadgeText?.setVisible(false);
    this.starterPackText?.setVisible(false);
    this.background && this.background?.setVisible(false);
    this.arrow?.setTexture('arrow-down-gold');
  }

  showPopupReward() {
    this.referralButton?.setVisible(true);
    this.dailySpinButton?.setVisible(true);
    this.airdropButton?.setVisible(true);
    this.starterPackButton?.setVisible(this.starterPackAvailable);
    this.referralText?.setVisible(true);
    this.airdropText?.setVisible(true);
    this.dailySpinText?.setVisible(true);
    this.starterPackText?.setVisible(this.starterPackAvailable);
    if (this.numberOfSpins) {
      this.dailySpinBadge?.setVisible(true);
      this.dailySpinBadgeText?.setVisible(true);
    }
    this.background && this.background?.setVisible(true);
    this.arrow?.setTexture('arrow-up-gold');
  }

  togglePopupReward() {
    if (this.referralButton?.visible) {
      this.hidePopupReward();
    } else {
      this.showPopupReward();
    }
  }

  openReferralPopup() {
    this.hidePopupReward();
    this.scene.popupReferralProgram?.open();
  }

  openDailySpinPopup() {
    this.hidePopupReward();
    this.scene.popupDailySpin?.open();
  }

  openAirdropPopup() {
    this.hidePopupReward();
    this.scene.popupAirdrop?.open();
  }

  openAugmentsPopup() {
    this.scene.popupAugments?.open();
  }

  openStarterPackPopup() {
    this.hidePopupReward();
    this.scene.popupStarterPack?.open();
  }
}

export default InfoButtons;
